import React from 'react';
import { Link2, AlertTriangle } from 'lucide-react';

/**
 * Démarches enchaînées derrière un même envoi (demande d'Emeni, 2026-08-30).
 *
 * Le client ne reçoit QU'UN bouton dans son email : celui de la Démarche principale. Les Démarches
 * cochées ici viennent ensuite, l'une après l'autre — en bas du formulaire, « Envoyer ma demande »
 * devient « Suivant », et le client enchaîne sans repasser par sa boîte mail.
 *
 * Chaque étape reste une Démarche à part entière : ses questions, son éventuel rendez-vous, sa réponse
 * enregistrée dès qu'elle est validée. Un client qui s'arrête en route ne fait rien perdre de ce qui a
 * déjà été répondu, et le lien de son email le ramène à l'étape où il en était.
 *
 * L'ordre affiché est l'ordre de sélection : cocher, c'est placer à la suite.
 */
export function DemarchesSuivantesPicker({ demarches, principalId, valeur, onChange }: {
    demarches: any[];
    principalId: string;
    valeur: string[];
    onChange: (ids: string[]) => void;
}) {
    const disponibles = demarches.filter((d) => d.id !== principalId);
    const principale = demarches.find((d) => d.id === principalId);
    // Une Démarche payante fait sortir le client vers le paiement : elle ne peut donc qu'être la
    // dernière. Le serveur applique la même règle et refuse l'envoi — ici on prévient avant le clic.
    const principalePayante = !!principale && principale.payment_type && principale.payment_type !== 'none';
    const estPayante = (d: any) => !!d.payment_type && d.payment_type !== 'none';
    const dejaUnePayante = valeur.some((id) => estPayante(disponibles.find((d) => d.id === id) || {}));

    const basculer = (id: string) => {
        onChange(valeur.includes(id) ? valeur.filter((v) => v !== id) : [...valeur, id]);
    };

    if (!principalId || disponibles.length === 0) return null;

    return (
        <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5">
                <Link2 size={11} /> Démarches à enchaîner ensuite (facultatif)
            </label>

            {principalePayante ? (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-start gap-1.5">
                    <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                    « {principale.name} » demande un paiement : elle doit rester la dernière étape, aucune
                    Démarche ne peut la suivre.
                </p>
            ) : (
                <>
                    <div className="space-y-1">
                        {disponibles.map((d) => {
                            const rang = valeur.indexOf(d.id);
                            const coche = rang >= 0;
                            // Un seul paiement par enchaînement : le client ne doit régler qu'une fois.
                            const bloquee = !coche && estPayante(d) && dejaUnePayante;
                            return (
                                <label key={d.id}
                                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs transition-all ${bloquee ? 'border-border opacity-40 cursor-not-allowed' : 'cursor-pointer'} ${coche ? 'bg-brand-500/10 border-brand-500' : 'border-border hover:bg-surface-soft'}`}>
                                    <input type="checkbox" checked={coche} disabled={bloquee}
                                        onChange={() => basculer(d.id)} className="accent-brand-600 w-3.5 h-3.5" />
                                    <span className="font-bold text-content flex-1">{d.name}</span>
                                    {estPayante(d) && <span className="text-[10px] text-content-muted">paiement</span>}
                                    {coche && <span className="text-[10px] font-black text-brand-600 dark:text-brand-400">Étape {rang + 2}</span>}
                                </label>
                            );
                        })}
                    </div>
                    <p className="text-[11px] text-content-muted leading-relaxed">
                        {valeur.length === 0
                            ? "Le client répond à une seule Démarche. Cochez-en d'autres pour qu'il les enchaîne à la suite, sans nouveau mail."
                            : `Le client remplira ${valeur.length + 1} étapes à la suite : le bouton du mail ouvre la première, puis « Suivant » le mène jusqu'à la dernière.`}
                    </p>
                </>
            )}
        </div>
    );
}
